import { Dispatch, FC, SetStateAction } from 'react';
import { QuestionGroup, QuestionType } from '@/remoteApi/types/form-template.types';
import { Box, Card, Checkbox, Collapse, Flex, Text, Title } from '@mantine/core';
import { QuestionRenderer } from './QuestionRenderer';
import { FormAnswerMap, FormOptions, QuantityPerDimensionForm } from './ProductTypeForm';
import { UUID } from 'crypto';
import { getFileSrc } from '@/utilities';
import { useDisclosure } from '@mantine/hooks';
import { ProductType } from '@/remoteApi/ProductTypeApi';
import { QuestionDimension } from './QuestionDimension';
import Image from 'next/image';

interface Props {
	productType: ProductType;
	questionGroupsCount: number;
	questionGroup: QuestionGroup;
	answersMap: Map<UUID, FormAnswerMap>;
	quantityPerDimension: QuantityPerDimensionForm;
	setQuantityPerDimension: Dispatch<SetStateAction<QuantityPerDimensionForm>>;
	isValidating: boolean;
	formOptions: FormOptions;
	handleOptionsChange: (
		key: keyof FormOptions,
		value: FormOptions[keyof FormOptions]
	) => void;
}

export const QuestionGroupCard: FC<Props> = ({
	productType,
	questionGroupsCount,
	questionGroup,
	answersMap,
	quantityPerDimension,
	setQuantityPerDimension,
	isValidating,
	formOptions,
	handleOptionsChange,
}) => {
	const [isAdvancedCollapsed, advancedCollapseControls] = useDisclosure();

	const advancedOptionsQuestion = questionGroup.questions.find(
		(question) => question.questionType === QuestionType.ADVANCED_OPTIONS
	);

	const preAdvancedQuestions = advancedOptionsQuestion
			? questionGroup.questions.filter(
					(question) => question.listOrder < advancedOptionsQuestion.listOrder
				)
			: questionGroup.questions,
		postAdvancedQuestions = advancedOptionsQuestion
			? questionGroup.questions.filter(
					(question) => question.listOrder > advancedOptionsQuestion.listOrder
				)
			: [];

	return (
		<Card mb="lg">
			<Title order={3} c="black" mb="md">
				{`Krok ${questionGroup.listOrder}/${questionGroupsCount} - ${questionGroup.name}`}
			</Title>
			{!!questionGroup.description && (
				<Text size="md" mb="md" c="dimmed">
					{questionGroup.description}
				</Text>
			)}
			<Flex
				gap="sm"
				direction={{ base: 'column-reverse', lg: 'row' }}
				align={{ base: 'stretch', lg: 'flex-start' }}
			>
				<Box style={{ flexGrow: 1 }}>
					{preAdvancedQuestions.map((question, idx, array) =>
						question.questionType === QuestionType.DIMENSIONS ? (
							<QuestionDimension
								key={question.id}
								withCustomDimensions={Boolean(Number(question.name))}
								productType={productType}
								quantityPerDimension={quantityPerDimension}
								setQuantityPerDimension={setQuantityPerDimension}
								isValidating={isValidating}
								formOptions={formOptions}
							/>
						) : (
							<QuestionRenderer
								key={question.id}
								question={question}
								answersMap={answersMap}
								isLast={idx === array.length - 1}
								isValidating={isValidating}
								hanldleOptionsChange={handleOptionsChange}
								setQuantityPerDimension={setQuantityPerDimension}
							/>
						)
					)}
					{!!advancedOptionsQuestion && (
						<Box my="md">
							<Checkbox
								label="Pokaż opcje zaawansowane"
								onChange={advancedCollapseControls.toggle}
							/>
							<Collapse in={isAdvancedCollapsed}>
								<Box pt="md">
									{postAdvancedQuestions.map((question, idx, array) =>
										question.questionType === QuestionType.DIMENSIONS ? (
											<QuestionDimension
												key={question.id}
												withCustomDimensions={Boolean(Number(question.name))}
												productType={productType}
												quantityPerDimension={quantityPerDimension}
												setQuantityPerDimension={setQuantityPerDimension}
												isValidating={isValidating}
												formOptions={formOptions}
											/>
										) : (
											<QuestionRenderer
												key={question.id}
												question={question}
												answersMap={answersMap}
												isLast={idx === array.length - 1}
												isValidating={isValidating}
												hanldleOptionsChange={handleOptionsChange}
												setQuantityPerDimension={setQuantityPerDimension}
											/>
										)
									)}
								</Box>
							</Collapse>
						</Box>
					)}
				</Box>
				{!!questionGroup.image && (
					<Image
						src={getFileSrc(questionGroup.image.path)}
						alt="Produkt"
						width={300}
						height={600}
					/>
				)}
			</Flex>
		</Card>
	);
};
